/**
 * Static, self-contained catalog of world countries used by the order form's
 * country selector. Deliberately NOT sourced from master-dealer-hub's
 * partner-locations taxonomy (which only has entries for countries that already
 * have a dealer) — every real-world country must be selectable, even ones with
 * zero dealers today (that's when the free-text dealer fallback kicks in).
 *
 * matchAliases lists the label(s) a matching partner-locations term is expected
 * to use, verified against sre000-master-dealer-hub/partners-migration.csv —
 * that data is a mix of German and English country names, not one language.
 */
export interface CountryCatalogEntry {
	code: string;
	name: string;
	matchAliases: string[];
}

export const COUNTRY_CATALOG: CountryCatalogEntry[] = [
	{ code: 'AF', name: 'Afghanistan', matchAliases: [ 'Afghanistan' ] },
	{ code: 'AL', name: 'Albania', matchAliases: [ 'Albania', 'Albanien' ] },
	{ code: 'DZ', name: 'Algeria', matchAliases: [ 'Algeria' ] },
	{ code: 'AD', name: 'Andorra', matchAliases: [ 'Andorra' ] },
	{ code: 'AO', name: 'Angola', matchAliases: [ 'Angola' ] },
	{
		code: 'AG',
		name: 'Antigua and Barbuda',
		matchAliases: [ 'Antigua and Barbuda' ],
	},
	{ code: 'AR', name: 'Argentina', matchAliases: [ 'Argentina' ] },
	{ code: 'AM', name: 'Armenia', matchAliases: [ 'Armenia' ] },
	{ code: 'AU', name: 'Australia', matchAliases: [ 'Australia' ] },
	{ code: 'AT', name: 'Austria', matchAliases: [ 'Austria', 'Österreich' ] },
	{
		code: 'AZ',
		name: 'Azerbaijan',
		matchAliases: [ 'Azerbaijan', 'Aserbaidschan' ],
	},
	{ code: 'BS', name: 'Bahamas', matchAliases: [ 'Bahamas' ] },
	{ code: 'BH', name: 'Bahrain', matchAliases: [ 'Bahrain' ] },
	{ code: 'BD', name: 'Bangladesh', matchAliases: [ 'Bangladesh' ] },
	{ code: 'BB', name: 'Barbados', matchAliases: [ 'Barbados' ] },
	{ code: 'BY', name: 'Belarus', matchAliases: [ 'Belarus' ] },
	{ code: 'BE', name: 'Belgium', matchAliases: [ 'Belgium', 'Belgien' ] },
	{ code: 'BZ', name: 'Belize', matchAliases: [ 'Belize' ] },
	{ code: 'BJ', name: 'Benin', matchAliases: [ 'Benin' ] },
	{ code: 'BT', name: 'Bhutan', matchAliases: [ 'Bhutan' ] },
	{ code: 'BO', name: 'Bolivia', matchAliases: [ 'Bolivia' ] },
	{
		code: 'BA',
		name: 'Bosnia and Herzegovina',
		matchAliases: [ 'Bosnia and Herzegovina', 'Bosnien und Herzegowina' ],
	},
	{ code: 'BW', name: 'Botswana', matchAliases: [ 'Botswana' ] },
	{ code: 'BR', name: 'Brazil', matchAliases: [ 'Brazil' ] },
	{ code: 'BN', name: 'Brunei', matchAliases: [ 'Brunei' ] },
	{ code: 'BG', name: 'Bulgaria', matchAliases: [ 'Bulgaria', 'Bulgarien' ] },
	{ code: 'BF', name: 'Burkina Faso', matchAliases: [ 'Burkina Faso' ] },
	{ code: 'BI', name: 'Burundi', matchAliases: [ 'Burundi' ] },
	{ code: 'CV', name: 'Cabo Verde', matchAliases: [ 'Cabo Verde' ] },
	{ code: 'KH', name: 'Cambodia', matchAliases: [ 'Cambodia' ] },
	{ code: 'CM', name: 'Cameroon', matchAliases: [ 'Cameroon' ] },
	{ code: 'CA', name: 'Canada', matchAliases: [ 'Canada' ] },
	{
		code: 'CF',
		name: 'Central African Republic',
		matchAliases: [ 'Central African Republic' ],
	},
	{ code: 'TD', name: 'Chad', matchAliases: [ 'Chad' ] },
	{ code: 'CL', name: 'Chile', matchAliases: [ 'Chile' ] },
	{ code: 'CN', name: 'China', matchAliases: [ 'China' ] },
	{ code: 'CO', name: 'Colombia', matchAliases: [ 'Colombia' ] },
	{ code: 'KM', name: 'Comoros', matchAliases: [ 'Comoros' ] },
	{ code: 'CG', name: 'Congo', matchAliases: [ 'Congo' ] },
	{ code: 'CD', name: 'Congo (DRC)', matchAliases: [ 'Congo (DRC)' ] },
	{ code: 'CR', name: 'Costa Rica', matchAliases: [ 'Costa Rica' ] },
	{ code: 'CI', name: "Côte d'Ivoire", matchAliases: [ "Côte d'Ivoire" ] },
	{ code: 'HR', name: 'Croatia', matchAliases: [ 'Croatia', 'Kroatien' ] },
	{ code: 'CU', name: 'Cuba', matchAliases: [ 'Cuba' ] },
	{ code: 'CY', name: 'Cyprus', matchAliases: [ 'Cyprus', 'Zypern' ] },
	{ code: 'CZ', name: 'Czechia', matchAliases: [ 'Czechia', 'Tschechien' ] },
	{ code: 'DK', name: 'Denmark', matchAliases: [ 'Denmark', 'Dänemark' ] },
	{ code: 'DJ', name: 'Djibouti', matchAliases: [ 'Djibouti' ] },
	{ code: 'DM', name: 'Dominica', matchAliases: [ 'Dominica' ] },
	{
		code: 'DO',
		name: 'Dominican Republic',
		matchAliases: [ 'Dominican Republic' ],
	},
	{ code: 'EC', name: 'Ecuador', matchAliases: [ 'Ecuador' ] },
	{ code: 'EG', name: 'Egypt', matchAliases: [ 'Egypt' ] },
	{ code: 'SV', name: 'El Salvador', matchAliases: [ 'El Salvador' ] },
	{
		code: 'GQ',
		name: 'Equatorial Guinea',
		matchAliases: [ 'Equatorial Guinea' ],
	},
	{ code: 'ER', name: 'Eritrea', matchAliases: [ 'Eritrea' ] },
	{ code: 'EE', name: 'Estonia', matchAliases: [ 'Estonia' ] },
	{ code: 'SZ', name: 'Eswatini', matchAliases: [ 'Eswatini' ] },
	{ code: 'ET', name: 'Ethiopia', matchAliases: [ 'Ethiopia' ] },
	{ code: 'FJ', name: 'Fiji', matchAliases: [ 'Fiji' ] },
	{ code: 'FI', name: 'Finland', matchAliases: [ 'Finland' ] },
	{ code: 'FR', name: 'France', matchAliases: [ 'France', 'Frankreich' ] },
	{ code: 'GA', name: 'Gabon', matchAliases: [ 'Gabon' ] },
	{ code: 'GM', name: 'Gambia', matchAliases: [ 'Gambia' ] },
	{ code: 'GE', name: 'Georgia', matchAliases: [ 'Georgia' ] },
	{ code: 'DE', name: 'Germany', matchAliases: [ 'Germany', 'Deutschland' ] },
	{ code: 'GH', name: 'Ghana', matchAliases: [ 'Ghana' ] },
	{ code: 'GR', name: 'Greece', matchAliases: [ 'Greece', 'Griechenland' ] },
	{ code: 'GD', name: 'Grenada', matchAliases: [ 'Grenada' ] },
	{ code: 'GT', name: 'Guatemala', matchAliases: [ 'Guatemala' ] },
	{ code: 'GN', name: 'Guinea', matchAliases: [ 'Guinea' ] },
	{ code: 'GW', name: 'Guinea-Bissau', matchAliases: [ 'Guinea-Bissau' ] },
	{ code: 'GY', name: 'Guyana', matchAliases: [ 'Guyana' ] },
	{ code: 'HT', name: 'Haiti', matchAliases: [ 'Haiti' ] },
	{ code: 'HN', name: 'Honduras', matchAliases: [ 'Honduras' ] },
	{ code: 'HU', name: 'Hungary', matchAliases: [ 'Hungary', 'Ungarn' ] },
	{ code: 'IS', name: 'Iceland', matchAliases: [ 'Iceland' ] },
	{ code: 'IN', name: 'India', matchAliases: [ 'India' ] },
	{ code: 'ID', name: 'Indonesia', matchAliases: [ 'Indonesia' ] },
	{ code: 'IR', name: 'Iran', matchAliases: [ 'Iran' ] },
	{ code: 'IQ', name: 'Iraq', matchAliases: [ 'Iraq', 'Irak' ] },
	{ code: 'IE', name: 'Ireland', matchAliases: [ 'Ireland' ] },
	{ code: 'IL', name: 'Israel', matchAliases: [ 'Israel' ] },
	{ code: 'IT', name: 'Italy', matchAliases: [ 'Italy', 'Italien' ] },
	{ code: 'JM', name: 'Jamaica', matchAliases: [ 'Jamaica' ] },
	{ code: 'JP', name: 'Japan', matchAliases: [ 'Japan' ] },
	{ code: 'JO', name: 'Jordan', matchAliases: [ 'Jordan' ] },
	{ code: 'KZ', name: 'Kazakhstan', matchAliases: [ 'Kazakhstan' ] },
	{ code: 'KE', name: 'Kenya', matchAliases: [ 'Kenya' ] },
	{ code: 'KI', name: 'Kiribati', matchAliases: [ 'Kiribati' ] },
	{ code: 'XK', name: 'Kosovo', matchAliases: [ 'Kosovo' ] },
	{ code: 'KW', name: 'Kuwait', matchAliases: [ 'Kuwait' ] },
	{ code: 'KG', name: 'Kyrgyzstan', matchAliases: [ 'Kyrgyzstan' ] },
	{ code: 'LA', name: 'Laos', matchAliases: [ 'Laos' ] },
	{ code: 'LV', name: 'Latvia', matchAliases: [ 'Latvia' ] },
	{ code: 'LB', name: 'Lebanon', matchAliases: [ 'Lebanon' ] },
	{ code: 'LS', name: 'Lesotho', matchAliases: [ 'Lesotho' ] },
	{ code: 'LR', name: 'Liberia', matchAliases: [ 'Liberia' ] },
	{ code: 'LY', name: 'Libya', matchAliases: [ 'Libya' ] },
	{ code: 'LI', name: 'Liechtenstein', matchAliases: [ 'Liechtenstein' ] },
	{ code: 'LT', name: 'Lithuania', matchAliases: [ 'Lithuania', 'Litauen' ] },
	{ code: 'LU', name: 'Luxembourg', matchAliases: [ 'Luxembourg' ] },
	{ code: 'MG', name: 'Madagascar', matchAliases: [ 'Madagascar' ] },
	{ code: 'MW', name: 'Malawi', matchAliases: [ 'Malawi' ] },
	{ code: 'MY', name: 'Malaysia', matchAliases: [ 'Malaysia' ] },
	{ code: 'MV', name: 'Maldives', matchAliases: [ 'Maldives' ] },
	{ code: 'ML', name: 'Mali', matchAliases: [ 'Mali' ] },
	{ code: 'MT', name: 'Malta', matchAliases: [ 'Malta' ] },
	{
		code: 'MH',
		name: 'Marshall Islands',
		matchAliases: [ 'Marshall Islands' ],
	},
	{ code: 'MR', name: 'Mauritania', matchAliases: [ 'Mauritania' ] },
	{ code: 'MU', name: 'Mauritius', matchAliases: [ 'Mauritius' ] },
	{ code: 'MX', name: 'Mexico', matchAliases: [ 'Mexico' ] },
	{ code: 'FM', name: 'Micronesia', matchAliases: [ 'Micronesia' ] },
	{ code: 'MD', name: 'Moldova', matchAliases: [ 'Moldova' ] },
	{ code: 'MC', name: 'Monaco', matchAliases: [ 'Monaco' ] },
	{ code: 'MN', name: 'Mongolia', matchAliases: [ 'Mongolia' ] },
	{ code: 'ME', name: 'Montenegro', matchAliases: [ 'Montenegro' ] },
	{ code: 'MA', name: 'Morocco', matchAliases: [ 'Morocco' ] },
	{ code: 'MZ', name: 'Mozambique', matchAliases: [ 'Mozambique' ] },
	{ code: 'MM', name: 'Myanmar', matchAliases: [ 'Myanmar' ] },
	{ code: 'NA', name: 'Namibia', matchAliases: [ 'Namibia' ] },
	{ code: 'NR', name: 'Nauru', matchAliases: [ 'Nauru' ] },
	{ code: 'NP', name: 'Nepal', matchAliases: [ 'Nepal' ] },
	{
		code: 'NL',
		name: 'Netherlands',
		matchAliases: [ 'Netherlands', 'Niederlande' ],
	},
	{ code: 'NZ', name: 'New Zealand', matchAliases: [ 'New Zealand' ] },
	{ code: 'NI', name: 'Nicaragua', matchAliases: [ 'Nicaragua' ] },
	{ code: 'NE', name: 'Niger', matchAliases: [ 'Niger' ] },
	{ code: 'NG', name: 'Nigeria', matchAliases: [ 'Nigeria' ] },
	{ code: 'KP', name: 'North Korea', matchAliases: [ 'North Korea' ] },
	{
		code: 'MK',
		name: 'North Macedonia',
		matchAliases: [ 'North Macedonia', 'Mazedonien' ],
	},
	{ code: 'NO', name: 'Norway', matchAliases: [ 'Norway', 'Norwegen' ] },
	{ code: 'OM', name: 'Oman', matchAliases: [ 'Oman' ] },
	{ code: 'PK', name: 'Pakistan', matchAliases: [ 'Pakistan' ] },
	{ code: 'PW', name: 'Palau', matchAliases: [ 'Palau' ] },
	{ code: 'PS', name: 'Palestine', matchAliases: [ 'Palestine' ] },
	{ code: 'PA', name: 'Panama', matchAliases: [ 'Panama' ] },
	{
		code: 'PG',
		name: 'Papua New Guinea',
		matchAliases: [ 'Papua New Guinea' ],
	},
	{ code: 'PY', name: 'Paraguay', matchAliases: [ 'Paraguay' ] },
	{ code: 'PE', name: 'Peru', matchAliases: [ 'Peru' ] },
	{ code: 'PH', name: 'Philippines', matchAliases: [ 'Philippines' ] },
	{ code: 'PL', name: 'Poland', matchAliases: [ 'Poland', 'Polen' ] },
	{ code: 'PT', name: 'Portugal', matchAliases: [ 'Portugal' ] },
	{ code: 'QA', name: 'Qatar', matchAliases: [ 'Qatar' ] },
	{ code: 'RO', name: 'Romania', matchAliases: [ 'Romania', 'Rumänien' ] },
	{ code: 'RU', name: 'Russia', matchAliases: [ 'Russia' ] },
	{ code: 'RW', name: 'Rwanda', matchAliases: [ 'Rwanda' ] },
	{
		code: 'KN',
		name: 'Saint Kitts and Nevis',
		matchAliases: [ 'Saint Kitts and Nevis' ],
	},
	{ code: 'LC', name: 'Saint Lucia', matchAliases: [ 'Saint Lucia' ] },
	{
		code: 'VC',
		name: 'Saint Vincent and the Grenadines',
		matchAliases: [ 'Saint Vincent and the Grenadines' ],
	},
	{ code: 'WS', name: 'Samoa', matchAliases: [ 'Samoa' ] },
	{ code: 'SM', name: 'San Marino', matchAliases: [ 'San Marino' ] },
	{
		code: 'ST',
		name: 'Sao Tome and Principe',
		matchAliases: [ 'Sao Tome and Principe' ],
	},
	{
		code: 'SA',
		name: 'Saudi Arabia',
		matchAliases: [ 'Saudi Arabia', 'KSA' ],
	},
	{ code: 'SN', name: 'Senegal', matchAliases: [ 'Senegal' ] },
	{ code: 'RS', name: 'Serbia', matchAliases: [ 'Serbia', 'Serbien' ] },
	{ code: 'SC', name: 'Seychelles', matchAliases: [ 'Seychelles' ] },
	{ code: 'SL', name: 'Sierra Leone', matchAliases: [ 'Sierra Leone' ] },
	{ code: 'SG', name: 'Singapore', matchAliases: [ 'Singapore' ] },
	{ code: 'SK', name: 'Slovakia', matchAliases: [ 'Slovakia', 'Slowakei' ] },
	{ code: 'SI', name: 'Slovenia', matchAliases: [ 'Slovenia' ] },
	{
		code: 'SB',
		name: 'Solomon Islands',
		matchAliases: [ 'Solomon Islands' ],
	},
	{ code: 'SO', name: 'Somalia', matchAliases: [ 'Somalia' ] },
	{
		code: 'ZA',
		name: 'South Africa',
		matchAliases: [ 'South Africa', 'Südafrika' ],
	},
	{ code: 'KR', name: 'South Korea', matchAliases: [ 'South Korea' ] },
	{ code: 'SS', name: 'South Sudan', matchAliases: [ 'South Sudan' ] },
	{ code: 'ES', name: 'Spain', matchAliases: [ 'Spain', 'Spanien' ] },
	{ code: 'LK', name: 'Sri Lanka', matchAliases: [ 'Sri Lanka' ] },
	{ code: 'SD', name: 'Sudan', matchAliases: [ 'Sudan' ] },
	{ code: 'SR', name: 'Suriname', matchAliases: [ 'Suriname' ] },
	{ code: 'SE', name: 'Sweden', matchAliases: [ 'Sweden' ] },
	{
		code: 'CH',
		name: 'Switzerland',
		matchAliases: [ 'Switzerland', 'Schweiz' ],
	},
	{ code: 'SY', name: 'Syria', matchAliases: [ 'Syria' ] },
	{ code: 'TW', name: 'Taiwan', matchAliases: [ 'Taiwan' ] },
	{ code: 'TJ', name: 'Tajikistan', matchAliases: [ 'Tajikistan' ] },
	{ code: 'TZ', name: 'Tanzania', matchAliases: [ 'Tanzania' ] },
	{ code: 'TH', name: 'Thailand', matchAliases: [ 'Thailand' ] },
	{ code: 'TL', name: 'Timor-Leste', matchAliases: [ 'Timor-Leste' ] },
	{ code: 'TG', name: 'Togo', matchAliases: [ 'Togo' ] },
	{ code: 'TO', name: 'Tonga', matchAliases: [ 'Tonga' ] },
	{
		code: 'TT',
		name: 'Trinidad and Tobago',
		matchAliases: [ 'Trinidad and Tobago' ],
	},
	{ code: 'TN', name: 'Tunisia', matchAliases: [ 'Tunisia' ] },
	{ code: 'TR', name: 'Turkey', matchAliases: [ 'Turkey' ] },
	{ code: 'TM', name: 'Turkmenistan', matchAliases: [ 'Turkmenistan' ] },
	{ code: 'TV', name: 'Tuvalu', matchAliases: [ 'Tuvalu' ] },
	{ code: 'UG', name: 'Uganda', matchAliases: [ 'Uganda' ] },
	{ code: 'UA', name: 'Ukraine', matchAliases: [ 'Ukraine' ] },
	{
		code: 'AE',
		name: 'United Arab Emirates',
		matchAliases: [ 'United Arab Emirates' ],
	},
	{ code: 'GB', name: 'United Kingdom', matchAliases: [ 'United Kingdom' ] },
	{ code: 'US', name: 'United States', matchAliases: [ 'United States' ] },
	{ code: 'UY', name: 'Uruguay', matchAliases: [ 'Uruguay' ] },
	{ code: 'UZ', name: 'Uzbekistan', matchAliases: [ 'Uzbekistan' ] },
	{ code: 'VU', name: 'Vanuatu', matchAliases: [ 'Vanuatu' ] },
	{ code: 'VA', name: 'Vatican City', matchAliases: [ 'Vatican City' ] },
	{ code: 'VE', name: 'Venezuela', matchAliases: [ 'Venezuela' ] },
	{ code: 'VN', name: 'Vietnam', matchAliases: [ 'Vietnam' ] },
	{ code: 'YE', name: 'Yemen', matchAliases: [ 'Yemen' ] },
	{ code: 'ZM', name: 'Zambia', matchAliases: [ 'Zambia' ] },
	{ code: 'ZW', name: 'Zimbabwe', matchAliases: [ 'Zimbabwe' ] },
];
